/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import Imgix from 'js/components/Imgix';

import { Grid, Typography2 } from '@coursera/cds-core';

import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

export type ProductHit = {
  partnerLogos?: Array<string>;
  partners?: Array<string>;
  imageUrl?: string;
  name?: string;
  objectID?: string;
  objectUrl?: string;
};

type Props = {
  productHit: ProductHit;
  productPosition: number;
  trackingName: string;
  disableMaxHeight?: boolean;
};

const styles = {
  root: (disableMaxHeight?: boolean) => css`
    display: block;
    height: 30%;
    max-height: 150px;
    ${disableMaxHeight &&
    `
      max-height: unset;
    `}
  `,
  searchCarouselCard: (disableMaxHeight?: boolean) => css`
    a {
      text-decoration: none;

      :hover,
      :focus {
        text-decoration: underline;
      }
    }

    &.cds {
      position: static;
      height: 30%;
      max-height: 150px;
      margin: 0 20px;
    }
    ${disableMaxHeight &&
    `
      &.cds {
        max-height: unset;
      }
    `}
  `,

  image: css`
    border-radius: 4px;
    box-shadow: 0 1px 2px rgb(0 0 0 / 25%);
  `,

  imageContainer: css`
    flex-shrink: 0;
    flex-grow: 0;
  `,

  cardContainer: css`
    padding: 13px;
  `,
};

const SearchCarouselCard = ({ productHit, productPosition, trackingName, disableMaxHeight }: Props) => {
  const { partnerLogos, partners, imageUrl, name, objectUrl } = productHit;
  const degreeObject = (product?: ProductHit) => {
    if (product?.objectID?.startsWith('base')) {
      return product.objectID.replace('base', 'degree');
    }
    return product?.objectID;
  };
  const objectID = degreeObject(productHit);
  return (
    <div className="rc-SearchCardCds cds" css={styles.searchCarouselCard(disableMaxHeight)}>
      <TrackedLink2
        atMostOnce
        css={styles.root(disableMaxHeight)}
        className="search-card-anchor-link"
        href={objectUrl}
        requireFullyVisible
        target="_blank"
        data={{ objectID, productPosition }}
        trackingName={trackingName}
        withVisibilityTracking
      >
        <Grid container data-e2e="search-card" spacing={16} css={styles.cardContainer}>
          <Grid item css={styles.imageContainer}>
            <Imgix
              alt={partners?.[0] || ''}
              css={styles.image}
              maxHeight={72}
              imgParams={{ fit: 'crop', auto: 'format,compress' }}
              src={trackingName.includes('project') ? imageUrl || '' : partnerLogos?.[0] || ''}
              maxWidth={72}
            />
          </Grid>
          <Grid item xs>
            <Typography2 component="h4" variant="subtitleMedium">
              {name}
            </Typography2>
            <Typography2 component="p" color="supportText" variant="bodySecondary">
              {partners?.[0]}
            </Typography2>
          </Grid>
        </Grid>
      </TrackedLink2>
    </div>
  );
};

export default SearchCarouselCard;
